import { Button, Dialog, Field, Icon, Input, Select } from '@dayon/design-system';
import type { MatrixPayload } from './types.ts';

/**
 * Invite an admin — the row, and only the row.
 *
 * "Invite" is the status the row is given, not a message anybody receives.
 * This creates an INVITED admin_user with no password and stops there.
 *
 * THE PLATFORM CAN SEND MAIL NOW, and this step still sends none — because
 * there is nothing to send yet. The message that matters carries the acceptance
 * LINK, and no link exists until somebody presses "Invite link" on the new row.
 * Saying so remains necessary for exactly the reason it always was: an operator
 * who walked away expecting the address to receive something would be waiting
 * on a message this step was never going to produce.
 */
export function InviteDialog({
  open,
  onClose,
  invite,
  onInvite,
  matrix,
  brands,
  errors,
  saving,
  canManage,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  invite: { name: string; email: string; role: string; scope: string; twofa: string };
  onInvite: (v: { name: string; email: string; role: string; scope: string; twofa: string }) => void;
  matrix: MatrixPayload | null;
  brands: { id: string; name: string }[];
  errors: Record<string, string>;
  saving: boolean;
  canManage: boolean;
  onSubmit: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite admin"
      wide
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={saving || !canManage}
            aria-describedby="invite-gap"
          >
            {/* Not "Send invite": nothing is sent. The button names the row it
                writes, which is the whole of what pressing it does. */}
            {saving ? 'Creating…' : 'Create invited admin'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet" id="invite-gap">
          <Icon name="info" />
          <div>
            {/* An `invited` admin_user row, invited_by = you, expiring in 72 hours, no password. */}
            This adds an <strong>invited</strong> admin: a real account with the role and scope
            chosen below, recorded as invited by you, expiring 72 hours from now, and with no
            password — so it appears on the roster immediately and nobody can sign in as it.{' '}
            {/* THE ROLE LIST SENTENCE USED TO NAME THE ENDPOINT IT ARRIVES ON. It read
                "comes from /api/access/matrix", which is this dialog describing its own
                plumbing to somebody standing in front of it: an operator cannot open that
                address, cannot quote it to anybody who will act on it, and learns nothing
                from it that "the live permission matrix" does not tell them better — the
                matrix is a thing on this very screen they can go and look at, and the claim
                that matters is that these roles are the configured ones rather than a list
                typed into this dialog. The read behind it is the GET of `/api/access/matrix`
                that AccessControl performs once and passes down here as the `matrix` prop —
                the same payload the permission matrix tab renders, which is why "the real
                ones" is true of both halves of the sentence. */}
            The role list comes from the live permission matrix and the brands from your session,
            so both are the real ones.
            <strong> Nothing is sent by this step.</strong> The invite EMAIL carries the
            acceptance link, and no link exists yet — so no message reaches the address you type
            here. Open the new row and press <strong>Invite link</strong>: that generates the
            single-use link, records the message that carries it, and tells you whether this
            environment will actually deliver it.
          </div>
        </div>

        <div className="halves">
          <Field label="Full name" error={errors['displayName']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={invite.name}
                onChange={(e) => onInvite({ ...invite, name: e.target.value })}
                placeholder="Maria Santos"
              />
            )}
          </Field>
          <Field label="Work email" error={errors['email']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="email"
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={invite.email}
                onChange={(e) => onInvite({ ...invite, email: e.target.value })}
                placeholder="maria.s@nocturneops.ph"
              />
            )}
          </Field>
        </div>

        <div className="halves">
          <Field
            label="Role"
            hint="One role per admin — roles are additive permission sets."
            error={errors['roleName']}
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={invite.role}
                onChange={(e) => onInvite({ ...invite, role: e.target.value })}
              >
                <option value="">Select a role…</option>
                {(matrix?.roles ?? []).map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Brand scope"
            hint="All brands is the absence of a scope restriction, not a brand of its own. It can be changed later from the drawer."
            error={errors['brandIds']}
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={invite.scope}
                onChange={(e) => onInvite({ ...invite, scope: e.target.value })}
              >
                {/* The value is the brand ID the route takes; the label is the
                    name the operator reads. An empty value is no restriction. */}
                <option value="">All brands</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field
          label="2FA method"
          hint="Text-message codes cannot be kept as a sign-in method yet and no text-message provider is connected, so only the authenticator app can actually be set up."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={invite.twofa}
              onChange={(e) => onInvite({ ...invite, twofa: e.target.value })}
            >
              <option value="Authenticator app">Authenticator app</option>
              {/* Drawn because the design specifies it, disabled because no
                  table could record it — see "Not yet available". */}
              <option value="SMS — Support only" disabled>
                SMS — Support only
              </option>
            </Select>
          )}
        </Field>

        <div className="text-subtle" style={{ fontSize: '12px' }}>
          The invite expires in 72 hours. Accepting it sets their password and enrols their
          authenticator in one step, for every role — so 2FA is set up before the first sign-in,
          exactly as this screen has always claimed.
        </div>
      </div>
    </Dialog>
  );
}
